import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, FileCheck2, AlertTriangle } from "lucide-react";

export const emailFromClientId = (cid) => {
  const tail = (cid || "").includes(":") ? cid.split(":").slice(1).join(":") : cid || "";
  return tail.includes("@") ? tail : "";
};

function CandidateRow({ c, selected, onSelect }) {
  const nameDiffers = c.booked_name && c.client_name && c.booked_name.trim() !== c.client_name.trim();
  return (
    <button
      type="button"
      onClick={() => onSelect(selected ? null : c.id)}
      className={`w-full text-left p-2.5 rounded-md border transition-colors ${selected ? "border-emerald-500 bg-emerald-50" : "border-zinc-200 bg-white hover:border-zinc-400"}`}
      data-testid={`intake-candidate-${c.id}`}
      aria-pressed={selected}
    >
      <div className="flex items-center gap-2 text-sm">
        <span className={`h-3.5 w-3.5 rounded-full border-2 shrink-0 ${selected ? "border-emerald-600 bg-emerald-600" : "border-zinc-300"}`} />
        <b className="text-zinc-900">{c.client_name || "(no name)"}</b>
        {nameDiffers && <span className="text-xs text-zinc-500">booked as {c.booked_name}</span>}
        <span className={`ml-auto text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border ${c.match === "email" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-amber-50 text-amber-900 border-amber-300"}`}>
          {c.match === "email" ? "Same email" : "Similar name"}
        </span>
      </div>
      <div className="text-xs text-zinc-600 mt-0.5 pl-5">
        {c.template_name || "Onboarding form"} · {c.submitted_at ? new Date(c.submitted_at).toLocaleDateString() : "—"} · {c.answer_count} answers · {c.client_email}
      </div>
    </button>
  );
}

export function IntakeFormPicker({ email, name, value, onChange }) {
  const [list, setList] = useState(null);

  useEffect(() => {
    if (!email && !name) { setList([]); return undefined; }
    let cancelled = false;
    setList(null);
    const t = setTimeout(() => {
      api.get("/assessments/intake-candidates", { params: { client_email: email || undefined, client_name: name || undefined } })
        .then((r) => {
          if (cancelled) return;
          const rows = r.data?.candidates || [];
          setList(rows);
          const firstEmail = rows.find((c) => c.match === "email");
          onChange(firstEmail ? firstEmail.id : null);
        })
        .catch(() => { if (!cancelled) setList([]); });
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, name]);

  if (list === null) {
    return (
      <div className="text-xs text-zinc-500 flex items-center gap-2" data-testid="intake-picker-loading">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Looking for onboarding forms…
      </div>
    );
  }
  if (list.length === 0) {
    return (
      <div className="p-3 rounded border border-zinc-200 bg-zinc-50 text-sm text-zinc-600" data-testid="intake-autolink-note-empty">
        No completed onboarding form found for this client yet — you&apos;ll fill in everything manually.
      </div>
    );
  }
  const picked = list.find((c) => c.id === value);
  return (
    <div className="space-y-1.5" data-testid="intake-picker">
      <div className="text-xs font-medium text-zinc-700 flex items-center gap-1.5">
        <FileCheck2 className="h-3.5 w-3.5 text-emerald-700" /> Onboarding form to pre-fill from
      </div>
      {list.map((c) => <CandidateRow key={c.id} c={c} selected={c.id === value} onSelect={onChange} />)}
      {picked?.match === "name" && (
        <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded p-2 flex gap-1.5" data-testid="intake-name-match-warning">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          This form was submitted with a different email ({picked.client_email}). Check it&apos;s the same person before linking.
        </div>
      )}
    </div>
  );
}
